// src/components/workloads/creation/WorkloadReviewStep.tsx
import React, { useMemo } from 'react';
import {
  Check,
  MapPin,
  FileText,
  Users,
  ClipboardList,
  SplitSquareVertical,
  AlertCircle,
} from 'lucide-react';
import { useWorkloadStore } from '../../../store/workloadStore';
import GoogleMapProvider from '../../map/GoogleMapProvider';
import BaseMap from '../../map/BaseMap';
import MarkerManager, { MarkerData } from '../../map/MarkerManager';

const WorkloadReviewStep: React.FC = () => {
  const { creation } = useWorkloadStore();

  const selectedAddresses = creation.addresses.filter((a) => a.selected);

  // Calculate workloads that will be created
  const workloadPreview = useMemo(() => {
    if (!creation.listSplitting.enabled || selectedAddresses.length === 0) {
      return [{ name: creation.name || 'Workload', count: selectedAddresses.length }];
    }

    const { maxSize, namePrefix, autoName } = creation.listSplitting;
    const numWorkloads = Math.ceil(selectedAddresses.length / maxSize);

    return Array.from({ length: numWorkloads }, (_, i) => {
      const start = i * maxSize;
      const count = Math.min(maxSize, selectedAddresses.length - start);
      const name = autoName
        ? `${creation.name || 'Workload'} - ${namePrefix} ${i + 1}`
        : `${namePrefix} ${i + 1}`;
      return { name, count };
    });
  }, [creation.listSplitting, creation.name, selectedAddresses.length]);

  // Convert addresses to markers
  const markers: MarkerData[] = useMemo(() => {
    return selectedAddresses.map((addr, index) => ({
      id: `addr_${index}`,
      position: addr.location,
      status: 'pending' as const,
      title: addr.street,
    }));
  }, [selectedAddresses]);

  // Calculate map center
  const mapCenter = useMemo(() => {
    if (selectedAddresses.length === 0) {
      return { lat: 39.8283, lng: -98.5795 };
    }

    const sum = selectedAddresses.reduce(
      (acc, addr) => ({
        lat: acc.lat + addr.location.lat,
        lng: acc.lng + addr.location.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / selectedAddresses.length,
      lng: sum.lng / selectedAddresses.length,
    };
  }, [selectedAddresses]);

  // Validation checks
  const validationIssues: string[] = [];
  if (!creation.name.trim()) {
    validationIssues.push('Workload name is required');
  }
  if (!creation.projectId) {
    validationIssues.push('Please select a project');
  }
  if (selectedAddresses.length === 0) {
    validationIssues.push('No addresses selected');
  }

  const isValid = validationIssues.length === 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Review & Create
        </h2>
        <p className="text-gray-600">
          Review your configuration before creating the workload{workloadPreview.length > 1 ? 's' : ''}.
        </p>
      </div>

      {/* Validation Issues */}
      {!isValid && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-red-800">Please fix the following issues:</h4>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {validationIssues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Configuration Summary */}
        <div className="space-y-4">
          {/* Basic Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-gray-400" />
              Basic Information
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Name:</dt>
                <dd className="font-medium text-gray-900">{creation.name || '(Not set)'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Type:</dt>
                <dd className="font-medium text-gray-900">
                  {creation.type === 'saturation'
                    ? 'Saturation Point'
                    : creation.type === 'csv_upload'
                    ? 'CSV Upload'
                    : 'Census Block'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Project:</dt>
                <dd className="font-medium text-gray-900">
                  {creation.projectId ? 'Selected' : '(Not set)'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Addresses */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-gray-400" />
              Addresses
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Total addresses:</dt>
                <dd className="font-medium text-gray-900">{creation.addresses.length}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Selected:</dt>
                <dd className="font-medium text-gray-900">{selectedAddresses.length}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Excluded:</dt>
                <dd className="font-medium text-gray-900">
                  {creation.addresses.length - selectedAddresses.length}
                </dd>
              </div>
            </dl>
          </div>

          {/* Survey */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <ClipboardList className="h-5 w-5 mr-2 text-gray-400" />
              Survey
            </h3>
            {creation.surveyId ? (
              <p className="text-sm text-green-600 flex items-center">
                <Check className="h-4 w-4 mr-1" />
                Survey linked
              </p>
            ) : (
              <p className="text-sm text-gray-500">No survey linked</p>
            )}
          </div>

          {/* Workloads to Create */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <SplitSquareVertical className="h-5 w-5 mr-2 text-gray-400" />
              Workloads to Create
            </h3>
            {creation.listSplitting.enabled ? (
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  {workloadPreview.length} workload{workloadPreview.length !== 1 ? 's' : ''} will
                  be created:
                </p>
                <ul className="space-y-2">
                  {workloadPreview.map((workload, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between text-sm bg-gray-50 rounded p-2"
                    >
                      <span className="font-medium text-gray-700">{workload.name}</span>
                      <span className="text-gray-500">{workload.count} addresses</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                1 workload with {selectedAddresses.length} addresses
              </p>
            )}
          </div>

          {/* Assignment */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <Users className="h-5 w-5 mr-2 text-gray-400" />
              Assignment
            </h3>
            {creation.assignedTo.length > 0 ? (
              <p className="text-sm text-green-600 flex items-center">
                <Check className="h-4 w-4 mr-1" />
                {creation.assignedTo.length} user{creation.assignedTo.length !== 1 ? 's' : ''}{' '}
                assigned
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                No users assigned yet (can be done after creation)
              </p>
            )}
          </div>
        </div>

        {/* Right: Map Preview */}
        <div>
          <div className="sticky top-4">
            <h3 className="font-medium text-gray-900 mb-3">Address Preview</h3>
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <GoogleMapProvider>
                <BaseMap center={mapCenter} zoom={13} height={400}>
                  <MarkerManager markers={markers} />
                </BaseMap>
              </GoogleMapProvider>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {selectedAddresses.length} addresses will be included
            </p>
          </div>
        </div>
      </div>

      {/* Final Confirmation */}
      {isValid && (
        <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-green-800">Ready to create!</h4>
              <p className="text-sm text-green-700 mt-1">
                Click "Create Workloads" below to create {workloadPreview.length} workload
                {workloadPreview.length !== 1 ? 's' : ''} with a total of{' '}
                {selectedAddresses.length} addresses.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkloadReviewStep;
